<template>
  <div id="call_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="(data, index) in callAnalyticsData"
        source="call_analytics"
        :key="`${data.key}_data_${index}`"
        :title="data.title"
        :description="range.label"
        :value="formatAnalyticsValue(data.value, data.unit)"
        :is-loading="data.isLoading"
        :title-tooltip="data.titleTooltip"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import { units } from "@/src/constants/analyticConstants.js";
import { formatAnalyticsValue } from "@/src/utils/analyticUtils.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const callAnalyticsData = ref([
  {
    key: "call_dialed",
    title: "Total calls dialed",
    value: "-",
    isLoading: true,
  },
  {
    key: "call_connected",
    title: "Calls connected",
    value: "-",
    isLoading: true,
  },
  {
    key: "connect_rate",
    title: "Connect rate",
    value: "-",
    isLoading: true,
    unit: units.RATE,
    titleTooltip: "Percentage of successfully connected calls out of the total calls dialed (excluding failed calls).",
  },
  {
    key: "average_duration",
    title: "Average call duration",
    value: "-",
    isLoading: true,
    unit: units.SECOND,
  },
]);

const callDialedCount = ref(0);
const callDialedFailedCount = ref(0);
const callConnectedCount = ref(0);

const setLoadingState = (key, state) => {
  const index = callAnalyticsData.value.findIndex((item) => item.key === key);
  if (index !== -1) {
    callAnalyticsData.value[index].isLoading = state;
  }
};

const updateValue = (key, newValue) => {
  const index = callAnalyticsData.value.findIndex((item) => item.key === key);
  if (index !== -1) {
    callAnalyticsData.value[index].value = newValue;
  }
};

const calculateConnectRate = () => {
  const totalDialedExcludingFailed = callDialedCount.value - callDialedFailedCount.value;
  if (totalDialedExcludingFailed > 0) {
    const rate = (callConnectedCount.value / totalDialedExcludingFailed) * 100;
    updateValue("connect_rate", rate.toFixed(2));
  } else {
    updateValue("connect_rate", "-");
  }
  setLoadingState("connect_rate", false);
};

const toFetchCallCount = async (action) => {
  try {
    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
      action: action,
    };

    const response = await AnalyticService.V2.fetchCallCount(params);
    if (response?.success) {
      updateValue(action, response.data.count || 0);
      
      if (action === "call_dialed") {
        callDialedCount.value = response.data.count || 0;
        callDialedFailedCount.value = response.data.failed_count || 0;
      } else if (action === "call_connected") {
        callConnectedCount.value = response.data.count || 0;
      }
    }
  } catch (error) {
    console.error(`An error occurred while fetching ${action} count: `, error);
    updateValue(action, "Error");
  } finally {
    setLoadingState(action, false);
  }
};

const toFetchCallAverageDuration = async () => {
  try {
    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.fetchCallAverageDuration(params);
    if (response?.success) {
      updateValue("average_duration", response?.data?.average_duration);
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching call avg duration: ",
      error,
    );
    updateValue("average_duration", "Error");
  } finally {
    setLoadingState("average_duration", false);
  }
};

const fetchAllData = async () => {
  try {
    callDialedCount.value = 0;
    callDialedFailedCount.value = 0;
    callConnectedCount.value = 0;

    callAnalyticsData.value.forEach((item) => {
      setLoadingState(item.key, true);
    });

    await Promise.all([
      toFetchCallCount("call_dialed"),
      toFetchCallCount("call_connected"),
      toFetchCallAverageDuration(),
    ]);

    calculateConnectRate();
  } catch (error) {
    console.error("An error occurred while fetching all data: ", error);
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) {
    fetchAllData();
  }
});

useAnalyticsRefresh(fetchAllData, [range, userIds, teamIds]);
</script>
